import { useRef } from "react";
import { ShareButton } from "./ShareButton";
import type { SocialCardFormat } from "../../lib/socialCard";

interface Cluster {
  label: string;
  size: number;
}

interface Props {
  clusters: Cluster[];
  eventTitle?: string;
}

// ── Social card content ───────────────────────────────────────────────────────

interface TopThemesSocialContentProps {
  clusters: Cluster[];
  eventTitle: string;
  format: SocialCardFormat;
}

function TopThemesSocialContent({ clusters, eventTitle, format }: TopThemesSocialContentProps) {
  const fontSize = format === "landscape" ? 20 : 18;
  const top5 = clusters.slice(0, 5);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#DB1270",
          marginBottom: 4,
        }}
      >
        Top Themen · {eventTitle}
      </div>
      {top5.map((cluster, idx) => (
        <div
          key={`${cluster.label}-${idx}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#E2E2E5",
              color: "#3A3A40",
              fontSize: 13,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {idx + 1}
          </span>
          <span
            style={{
              flex: 1,
              fontSize,
              fontWeight: 500,
              color: "#000000",
              lineHeight: 1.4,
            }}
          >
            {cluster.label}
          </span>
          <span
            style={{
              flexShrink: 0,
              background: "rgba(219,18,112,0.10)",
              color: "#DB1270",
              borderRadius: 9999,
              padding: "2px 10px",
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {cluster.size}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TopThemes({ clusters, eventTitle = "" }: Props) {
  // Track the cluster IDs we've already seen — stagger only newly added clusters.
  const seenIdsRef = useRef<Set<string>>(new Set());

  return (
    <div className="bg-rc-paper border border-rc-fog rounded-2xl p-6 md:p-8 h-full">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="text-xl md:text-2xl font-bold text-rc-black">
          Top Themen
        </div>
        {clusters.length > 0 && (
          <ShareButton
            label="Top Themen teilen"
            title={eventTitle}
            cardContent={(fmt) => (
              <TopThemesSocialContent
                clusters={clusters}
                eventTitle={eventTitle}
                format={fmt}
              />
            )}
          />
        )}
      </div>

      {clusters.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="text-[14px] text-rc-slate leading-[1.55]">
            Noch keine Cluster — das Clustering startet, sobald genug Antworten eingehen.
          </p>
        </div>
      ) : (
        <ol className="space-y-3">
          {clusters.map((cluster, idx) => {
            const key = `${cluster.label}-${idx}`;
            const isNew = !seenIdsRef.current.has(key);
            if (isNew) seenIdsRef.current.add(key);

            return (
              <li
                key={key}
                className={`flex items-center gap-3 ${isNew ? "animate-fade-up" : ""}`}
                style={isNew ? { animationDelay: `${idx * 60}ms` } : undefined}
              >
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-rc-fog/60 text-[12px] font-bold text-rc-charcoal flex items-center justify-center tabular-nums">
                  {idx + 1}
                </span>
                <span className="flex-1 text-[15px] font-medium text-rc-black leading-[1.4] min-w-0">
                  {cluster.label}
                </span>
                <span className="flex-shrink-0 inline-flex items-center rounded-pill bg-rc-berry/10 px-2.5 py-0.5 text-[12px] font-semibold tabular-nums text-rc-berry-700">
                  {cluster.size}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
